import React from 'react';
import PropTypes from 'prop-types';
import './FormField.css';

/**
 * FormField Component
 * 
 * A wrapper component that provides label, help text, and error message for form inputs.
 * Works with Input, Textarea, Select, Checkbox, Radio, etc.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Form input component
 * @param {string} props.label - Label text
 * @param {string} props.htmlFor - ID of the input element (for label)
 * @param {string} props.helpText - Help/description text
 * @param {string} props.errorMessage - Error message to display
 * @param {boolean} props.required - Shows required indicator
 * @param {boolean} props.fullWidth - Full width field
 * @param {string} props.className - Additional CSS classes
 */
const FormField = ({
  children,
  label,
  htmlFor,
  helpText,
  errorMessage,
  required = false,
  fullWidth = false,
  className = '',
  ...props
}) => {
  const containerClasses = [
    'form-field',
    fullWidth ? 'form-field--full-width' : '',
    errorMessage ? 'form-field--error' : '',
    className
  ].filter(Boolean).join(' ');

  const helpTextId = helpText && htmlFor ? `${htmlFor}-help` : undefined;
  const errorId = errorMessage && htmlFor ? `${htmlFor}-error` : undefined;

  // Clone children and add aria-describedby if htmlFor is provided
  const enhancedChildren = htmlFor
    ? React.cloneElement(React.Children.only(children), {
        id: htmlFor,
        'aria-describedby': [helpTextId, errorId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': errorMessage ? 'true' : undefined,
      })
    : children;

  return (
    <div className={containerClasses} {...props}>
      {label && (
        <label htmlFor={htmlFor} className="form-field__label">
          {label}
          {required && (
            <span className="form-field__required" aria-label="required"> *</span>
          )}
        </label>
      )}

      <div className="form-field__input">
        {enhancedChildren}
      </div>

      {helpText && !errorMessage && (
        <p id={helpTextId} className="form-field__help">
          {helpText}
        </p>
      )}

      {errorMessage && (
        <p id={errorId} className="form-field__error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

FormField.propTypes = {
  children: PropTypes.element.isRequired,
  label: PropTypes.node,
  htmlFor: PropTypes.string,
  helpText: PropTypes.string,
  errorMessage: PropTypes.string,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

export default FormField;
